import React, { useState, useEffect } from 'react';
import { RefreshCw, ClipboardList, Filter, X, User, Tag, Clock, FileText, Code2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useGetLogsQuery, type ActivityLog } from '../store/logsApi';

const LIMIT = 20;

const ACTION_CFG: Record<string, { label: string; cls: string }> = {
  create: { label: 'Crear', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  update: { label: 'Editar', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  delete: { label: 'Eliminar', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  login: { label: 'Login', cls: 'bg-teal-100 text-teal-700 border-teal-200' },
  logout: { label: 'Logout', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const LEVEL_CFG: Record<string, { label: string; cls: string; rowCls: string; dot: string }> = {
  info: { label: 'Info', cls: 'bg-slate-100 text-slate-600 border-slate-200', rowCls: '', dot: 'bg-slate-400' },
  warning: { label: 'Advertencia', cls: 'bg-amber-100 text-amber-700 border-amber-200', rowCls: 'bg-amber-50/40', dot: 'bg-amber-500' },
  error: { label: 'Error', cls: 'bg-rose-100 text-rose-700 border-rose-200', rowCls: 'bg-rose-50/40', dot: 'bg-rose-500' },
};

const ENTITY_CFG: Record<string, { label: string; cls: string }> = {
  product: { label: 'Producto', cls: 'bg-violet-100 text-violet-700 border-violet-200' },
  category: { label: 'Categoría', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  product_image: { label: 'Imagen', cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  product_sizes: { label: 'Talles', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  session: { label: 'Sesión', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

function fmtRelative(unix: number): string {
  const diff = Math.floor(Date.now() / 1000) - unix;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

function fmtFull(unix: number): string {
  return new Date(unix * 1000).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function fmtMetaPreview(raw: string | null): string {
  if (!raw) return '—';
  try {
    const obj = JSON.parse(raw) as Record<string, unknown>;
    return Object.entries(obj).slice(0, 3).map(([k, v]) => {
      if (k === 'updatedFields' && Array.isArray(v)) return `Campos: ${(v as string[]).join(', ')}`;
      if (k === 'sizeCount') return `Talles: ${v}`;
      if (k === 'reorderedCount') return `Reord.: ${v}`;
      if (k === 'isPrimary') return `Primaria: ${v ? 'sí' : 'no'}`;
      return `${k}: ${v}`;
    }).join(' · ');
  } catch {
    return '—';
  }
}

function getPageRange(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | null)[] = [1];
  if (current > 3) pages.push(null);
  const lo = Math.max(2, current - 1);
  const hi = Math.min(total - 1, current + 1);
  for (let i = lo; i <= hi; i++) pages.push(i);
  if (current < total - 2) pages.push(null);
  pages.push(total);
  return pages;
}

function Badge({ label, cls }: { label: string; cls: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

// ─── Modal de detalle ────────────────────────────────────────────────────────

function Section({ icon: Icon, title, children }: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">{title}</span>
      </div>
      <div className="pl-5">{children}</div>
    </div>
  );
}

function MetaTable({ raw }: { raw: string | null }) {
  if (!raw) return <span className="text-xs text-ink-faint">—</span>;
  let obj: Record<string, unknown>;
  try { obj = JSON.parse(raw) as Record<string, unknown>; } catch { return <span className="text-xs text-ink-faint">JSON inválido</span>; }
  const entries = Object.entries(obj);
  if (!entries.length) return <span className="text-xs text-ink-faint">—</span>;
  return (
    <div className="rounded-xl border border-page-border overflow-hidden">
      {entries.map(([k, v], i) => (
        <div key={k} className={`flex gap-3 px-3 py-2 text-xs ${i % 2 === 0 ? 'bg-page-surface' : 'bg-white'}`}>
          <span className="font-mono font-semibold text-ink-muted w-32 flex-shrink-0 truncate">{k}</span>
          <span className="text-ink break-all">
            {Array.isArray(v) ? (v as string[]).join(', ') : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

function LogDetailModal({ log, onClose }: { log: ActivityLog; onClose: () => void }) {
  const actionCfg = ACTION_CFG[log.action] ?? { label: log.action, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  const entityCfg = ENTITY_CFG[log.entity] ?? { label: log.entity, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  const levelCfg = LEVEL_CFG[log.level] ?? LEVEL_CFG.info;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-ink/40 backdrop-blur-md animate-fade-in" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-page-border flex flex-col animate-fade-scale z-10 max-h-[90vh]">

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-page-border flex-shrink-0">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge label={actionCfg.label} cls={actionCfg.cls} />
              <Badge label={entityCfg.label} cls={entityCfg.cls} />
              {log.level !== 'info' && (
                <Badge label={levelCfg.label} cls={levelCfg.cls} />
              )}
            </div>
            <p className="text-[11px] text-ink-muted font-mono">
              {fmtRelative(log.createdAt)} · {fmtFull(log.createdAt)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-faint hover:text-ink p-1 hover:bg-page-surface rounded-lg transition-colors flex-shrink-0 ml-3"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Entidad */}
          <Section icon={Tag} title="Entidad">
            <div className="space-y-1">
              <div className="flex gap-2 text-xs">
                <span className="text-ink-muted w-16 flex-shrink-0">Nombre</span>
                <span className="text-ink font-medium">{log.entityName || '—'}</span>
              </div>
              {log.entityId && (
                <div className="flex gap-2 text-xs">
                  <span className="text-ink-muted w-16 flex-shrink-0">ID</span>
                  <span className="text-ink font-mono text-[11px] break-all">{log.entityId}</span>
                </div>
              )}
            </div>
          </Section>

          {/* Usuario */}
          <Section icon={User} title="Usuario">
            {log.user ? (
              <div className="space-y-0.5">
                <p className="text-xs font-semibold text-ink">{log.user.name}</p>
                <p className="text-[11px] text-ink-muted">{log.user.email}</p>
                <p className="text-[10px] text-ink-faint uppercase tracking-wider font-semibold mt-0.5">{log.user.role}</p>
              </div>
            ) : (
              <span className="text-xs text-ink-faint">—</span>
            )}
          </Section>

          {/* Descripción */}
          {log.description && (
            <Section icon={FileText} title="Descripción">
              <p className="text-xs text-ink leading-relaxed">{log.description}</p>
            </Section>
          )}

          {/* Metadata */}
          <Section icon={Code2} title="Metadata">
            <MetaTable raw={log.metadata} />
          </Section>

          {/* ID del registro */}
          <Section icon={Clock} title="ID del registro">
            <p className="text-[11px] font-mono text-ink-muted break-all">{log.id}</p>
          </Section>

        </div>
      </div>
    </div>
  );
}

// ─── PageBtn ─────────────────────────────────────────────────────────────────

function PageBtn({ onClick, disabled, active, children, 'aria-label': ariaLabel }: {
  onClick: () => void; disabled: boolean; active?: boolean;
  children: React.ReactNode; 'aria-label'?: string;
}) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} aria-label={ariaLabel}
      className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition-all border ${active
          ? 'bg-brand-primary text-white border-brand-primary shadow-sm shadow-brand-glow'
          : disabled
            ? 'text-ink-faint border-transparent cursor-not-allowed opacity-40'
            : 'text-ink-muted border-page-border hover:border-brand-border hover:text-brand-primary hover:bg-brand-surface'
        }`}
    >
      {children}
    </button>
  );
}

// ─── Fila de tabla ───────────────────────────────────────────────────────────

function LogRow({ log, onClick }: { log: ActivityLog; onClick: () => void }) {
  const actionCfg = ACTION_CFG[log.action] ?? { label: log.action, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  const entityCfg = ENTITY_CFG[log.entity] ?? { label: log.entity, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  const levelCfg = LEVEL_CFG[log.level] ?? LEVEL_CFG.info;

  return (
    <tr
      onClick={onClick}
      className={`transition-colors cursor-pointer hover:brightness-95 ${levelCfg.rowCls || 'hover:bg-brand-surface/40'}`}
    >
      <td
        className="px-4 py-3 text-ink-muted whitespace-nowrap font-mono text-[11px]"
        title={fmtFull(log.createdAt)}
      >
        <div className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${levelCfg.dot}`} />
          {fmtRelative(log.createdAt)}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <Badge label={actionCfg.label} cls={actionCfg.cls} />
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <Badge label={entityCfg.label} cls={entityCfg.cls} />
      </td>
      <td className="px-4 py-3 text-ink font-medium max-w-[140px]">
        <span className="block truncate">{log.entityName || '—'}</span>
      </td>
      <td className="px-4 py-3 text-ink-muted max-w-[120px]">
        <span className="block truncate">{log.user?.name || '—'}</span>
      </td>
      <td className="px-4 py-3 text-ink-muted max-w-[220px]">
        <span className="block truncate text-[11px]">
          {log.description || fmtMetaPreview(log.metadata)}
        </span>
      </td>
    </tr>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

export const Logs: React.FC = () => {
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [level, setLevel] = useState('');
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);

  const { data, isFetching, refetch } = useGetLogsQuery(
    { page, limit: LIMIT, entity: entity || undefined, action: action || undefined, level: level || undefined },
    { refetchOnMountOrArgChange: true },
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const hasFilters = entity !== '' || action !== '' || level !== '';

  return (
    <div className="space-y-5">

      {/* Header */}
      <header className="flex justify-between items-start border-b border-page-border pb-5">
        <div>
          <span className="text-xs font-semibold text-violet-600 uppercase tracking-widest">Superadmin</span>
          <h1 className="font-heading font-bold text-2xl lg:text-3xl text-ink mt-1">Registros de Actividad</h1>
          <p className="text-sm text-ink-muted mt-0.5">Historial completo de acciones en el panel</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium bg-page-surface hover:bg-page-panel border border-page-border text-ink-muted hover:text-ink transition-colors disabled:opacity-50 flex-shrink-0 mt-1"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refrescar</span>
        </button>
      </header>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />

        <select
          value={entity}
          onChange={(e) => { setEntity(e.target.value); setPage(1); }}
          className="text-xs bg-white border border-page-border rounded-lg px-3 py-2 text-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/30 cursor-pointer"
        >
          <option value="">Todas las entidades</option>
          <option value="product">Productos</option>
          <option value="category">Categorías</option>
          <option value="product_image">Imágenes</option>
          <option value="product_sizes">Talles</option>
          <option value="session">Sesiones</option>
        </select>

        <select
          value={action}
          onChange={(e) => { setAction(e.target.value); setPage(1); }}
          className="text-xs bg-white border border-page-border rounded-lg px-3 py-2 text-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/30 cursor-pointer"
        >
          <option value="">Todas las acciones</option>
          <option value="create">Crear</option>
          <option value="update">Editar</option>
          <option value="delete">Eliminar</option>
          <option value="login">Login</option>
          <option value="logout">Logout</option>
        </select>

        <select
          value={level}
          onChange={(e) => { setLevel(e.target.value); setPage(1); }}
          className="text-xs bg-white border border-page-border rounded-lg px-3 py-2 text-ink focus:outline-none focus:ring-2 focus:ring-brand-primary/30 cursor-pointer"
        >
          <option value="">Todos los niveles</option>
          <option value="info">Info</option>
          <option value="warning">Advertencia</option>
          <option value="error">Error</option>
        </select>

        {hasFilters && (
          <button
            onClick={() => { setEntity(''); setAction(''); setLevel(''); setPage(1); }}
            className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink px-2 py-1.5 rounded-lg hover:bg-page-surface transition-colors"
          >
            <X className="w-3 h-3" />
            Limpiar
          </button>
        )}

        {data != null && (
          <span className="ml-auto text-[11px] text-ink-faint">
            {data.total} registro{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-page-border shadow-sm overflow-hidden">
        {isFetching && !data ? (
          <div className="flex items-center justify-center h-40 text-sm text-ink-muted">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Cargando registros...
          </div>
        ) : !data?.items.length ? (
          <div className="flex flex-col items-center justify-center h-40 text-sm text-ink-muted space-y-1">
            <ClipboardList className="w-8 h-8 text-ink-faint mb-1" />
            <p>Sin registros</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-page-border bg-page-surface">
                  {['Fecha', 'Acción', 'Entidad', 'Nombre', 'Usuario', 'Detalle'].map((col) => (
                    <th key={col} className="text-left px-4 py-3 font-semibold text-ink-muted uppercase tracking-wider text-[10px] whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y divide-page-border ${isFetching ? 'opacity-60' : ''} transition-opacity`}>
                {data.items.map((log) => (
                  <LogRow key={log.id} log={log} onClick={() => setSelectedLog(log)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-ink-faint hidden sm:block">
            {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, data?.total ?? 0)} de {data?.total ?? 0}
          </span>

          <div className="flex items-center gap-1 mx-auto sm:mx-0">
            <PageBtn onClick={() => setPage(p => p - 1)} disabled={page <= 1} aria-label="Anterior">
              <ChevronLeft className="w-3.5 h-3.5" />
            </PageBtn>

            {getPageRange(page, totalPages).map((p, i) =>
              p === null ? (
                <span key={`e-${i}`} className="w-8 text-center text-xs text-ink-faint select-none">…</span>
              ) : (
                <PageBtn key={p} onClick={() => setPage(p)} disabled={false} active={p === page}>
                  {p}
                </PageBtn>
              )
            )}

            <PageBtn onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} aria-label="Siguiente">
              <ChevronRight className="w-3.5 h-3.5" />
            </PageBtn>
          </div>

          <span className="text-[11px] text-ink-faint hidden sm:block">Página {page} de {totalPages}</span>
        </div>
      )}

      {/* Modal */}
      {selectedLog && (
        <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
};
